// Page Loading Indicator System
class PageLoadingIndicator {
  constructor() {
    this.isLoading = false;
    this.loadingOverlay = null;
    this.minLoadingTime = 200; // Minimum time to show spinner (prevents flashing)
    this.loadingStartTime = null;
    this.init();
  }

  init() {
    // Create loading overlay
    this.createLoadingOverlay();

    // Attach navigation event listeners
    this.attachNavigationListeners();

    // Hide loading indicator when page is fully loaded
    this.attachPageLoadListeners();
  }

  createLoadingOverlay() {
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.id = 'page-loading-overlay';
    this.loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, #0d6efd 0%, #0dcaf0 50%, #0d6efd 100%);
      background-size: 200% 100%;
      animation: loading-progress 1.5s linear infinite;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.3s ease;
      display: none;
    `;

    // Add CSS animation via style tag
    if (!document.getElementById('page-loading-styles')) {
      const style = document.createElement('style');
      style.id = 'page-loading-styles';
      style.textContent = `
        @keyframes loading-progress {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .page-loading-active {
          cursor: wait !important;
        }

        .page-loading-active * {
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(this.loadingOverlay);
  }

  attachNavigationListeners() {
    // Handle link clicks
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (link && this.shouldShowLoadingForLink(link)) {
        this.showLoading();
      }
    });

    // Handle form submissions that navigate
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (this.shouldShowLoadingForForm(form)) {
        this.showLoading();
      }
    });
  }

  attachPageLoadListeners() {
    // Hide loading when page starts loading (beforeunload)
    window.addEventListener('beforeunload', () => {
      // Don't hide here, let it show during navigation
    });

    // Hide loading when page finishes loading
    window.addEventListener('load', () => {
      this.hideLoading();
    });

    // Hide loading when DOM is ready (faster than full load)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.hideLoading(), 100);
      });
    }

    // Hide loading on page show (back/forward navigation)
    window.addEventListener('pageshow', () => {
      this.hideLoading();
    });
  }

  shouldShowLoadingForLink(link) {
    const href = link.getAttribute('href');

    // Skip if no href or javascript/hash/data links
    if (!href || href.startsWith('javascript:') || href.startsWith('#') || href.startsWith('data:')) {
      return false;
    }

    // Skip if external link
    if (href.startsWith('http') && !href.includes(window.location.host)) {
      return false;
    }

    // Skip if link has target="_blank"
    if (link.getAttribute('target') === '_blank') {
      return false;
    }

    return true;
  }

  shouldShowLoadingForForm(form) {
    // Skip if form has Altcha widget (CAPTCHA forms)
    if (form.querySelector('altcha-widget')) {
      return false;
    }

    // Skip if form method is GET and action is not set (stays on same page)
    const method = (form.getAttribute('method') || 'GET').toLowerCase();
    const action = form.getAttribute('action');

    if (method === 'get' && !action) {
      return false;
    }

    return true;
  }

  showLoading() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.loadingStartTime = Date.now();

    // Add loading class to body
    document.body.classList.add('page-loading-active');

    // Show and animate the loading bar
    this.loadingOverlay.style.display = 'block';

    // Small delay to ensure display is set
    setTimeout(() => {
      this.loadingOverlay.style.opacity = '1';
    }, 10);
  }

  hideLoading() {
    if (!this.isLoading) return;

    const timeElapsed = Date.now() - this.loadingStartTime;
    const remainingTime = Math.max(0, this.minLoadingTime - timeElapsed);

    setTimeout(() => {
      this.isLoading = false;

      // Remove loading class from body
      document.body.classList.remove('page-loading-active');

      // Hide the loading bar
      this.loadingOverlay.style.opacity = '0';

      // Hide element after transition
      setTimeout(() => {
        if (!this.isLoading) {
          this.loadingOverlay.style.display = 'none';
        }
      }, 300);
    }, remainingTime);
  }
}

// Initialize the page loading indicator
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PageLoadingIndicator();
  });
} else {
  new PageLoadingIndicator();
}